import { FC, Fragment, useCallback } from 'react'
import { GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { broadcastColor, useExpressBroadcastCard } from './ExpressBroadcastCard.context'

import Button from 'components/common/Button/Button'
import { ExpressDispatchClientState } from 'models/deal'
import { QueryStatus } from 'components/common/QueryStatus'
import StopIcon from '@mui/icons-material/Stop'
import { Typography } from '@mui/material'
import classnames from 'classnames'
import styles from './ExpressBroadcastCard.module.sass'
import { useActionPopup } from 'utils/hooks'
import { useTranslation } from 'react-i18next'

/** Invite outcomes shown only when something landed in them */
const closedInviteCounts = ['lost', 'expired', 'revoked'] as const

export const ExpressBroadcastCardController: FC = () => {
  const { t } = useTranslation(['express_broadcast_card'])
  const { showConfirm } = useActionPopup()
  const {
    broadcastAcceptsAnswers,
    canRevoke,
    cardColor,
    dealId,
    expressBroadcast: broadcast,
    expressDispatch: client,
    formatDateTime,
    minutesLeft,
    revokeBroadcastMutation,
    timezone,
  } = useExpressBroadcastCard()

  const handleRevoke = useCallback(async () => {
    const confirmResponse = await showConfirm(
      t('express_broadcast_card:revoke_prompt.description'),
      {
        title: t('express_broadcast_card:revoke_prompt.title'),
        confirmAcceptText: t('express_broadcast_card:revoke_prompt.proceed'),
      }
    )

    if (!confirmResponse) return

    revokeBroadcastMutation.mutate({ orderId: dealId })
  }, [dealId, revokeBroadcastMutation, showConfirm, t])

  if (!client) return <Fragment />

  return (
    <div className={classnames(styles[cardColor], styles.expressBroadcastWrapper)}>

      {/** What the client is shown, with the deadline */}
      <div className={styles.statusDetails}>

        <div className={styles.lines}>

          <Typography variant="text-xs" color={GRAY_800}>{t('express_broadcast_card:title')}</Typography>

          <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>
            {t(`express_broadcast_card:client_state.${client.state}`)}
          </Typography>

          {!!client.attempt &&
            <Typography variant="text-sm" color={GRAY_800}>
              {t('express_broadcast_card:current_attempt', { attempt: t(`express_broadcast_card:attempt.${client.attempt}`) })}
            </Typography>
          }

          <Typography variant="text-sm" color={GRAY_800}>
            {t('express_broadcast_card:deadline', { dateTime: formatDateTime(client.expiresAt), timezone })}
          </Typography>

          {client.state === ExpressDispatchClientState.SEARCHING &&
            <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
              {minutesLeft > 0
                ? t('express_broadcast_card:minutes_left', { value: minutesLeft })
                : t('express_broadcast_card:deadline_passed')
              }
            </Typography>
          }

        </div>

        <div className={styles.verticalDivider} />

      </div>

      {/** The latest broadcast */}
      <div className={styles.broadcastWrapper}>
        {!!broadcast &&
          <div className={classnames(styles.contentWrapper, styles[`${broadcastColor(broadcast)}Border`])}>

            <div className={styles.header}>

              <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
                {t('express_broadcast_card:broadcast_title', { attemptNumber: broadcast.attemptNumber })}
              </Typography>

              <Typography variant="text-xs" color={GRAY_800} title={broadcast.id}>
                {broadcast.id.slice(0, 8)}
              </Typography>

              <span className={classnames(styles.tag, styles[`${broadcastColor(broadcast)}Background`])}>
                {t(`express_broadcast_card:broadcast_state.${broadcast.state}`)}
              </span>

              {broadcast.overdue &&
                <span className={classnames(styles.tag, styles.redBackground)}>{t('express_broadcast_card:overdue')}</span>
              }

            </div>

            {broadcast.overdue &&
              <Typography variant="text-sm" color={GRAY_800}>{t('express_broadcast_card:overdue_hint')}</Typography>
            }

            <Typography variant="text-sm" color={GRAY_800}>
              {t('express_broadcast_card:opened_at', { dateTime: formatDateTime(broadcast.openedAt) })}
              {!!broadcast.closedAt && ` · ${t('express_broadcast_card:closed_at', { dateTime: formatDateTime(broadcast.closedAt) })}`}
            </Typography>

            <div className={styles.counts}>

              <Typography variant="text-sm" color={GRAY_900}>
                {t('express_broadcast_card:invites.total', { value: broadcast.invites.total })}
              </Typography>

              <Typography variant="text-sm" color={GRAY_900}>
                {t(`express_broadcast_card:invites.${broadcastAcceptsAnswers ? 'waiting' : 'no_answer'}`, { value: broadcast.invites.waiting })}
              </Typography>

              <Typography variant="text-sm" color={GRAY_900}>
                {t('express_broadcast_card:invites.dismissed', { value: broadcast.invites.dismissed })}
              </Typography>

              {closedInviteCounts.filter((key) => broadcast.invites[key] > 0).map((key) => (
                <Typography key={key} variant="text-sm" color={GRAY_900}>
                  {t(`express_broadcast_card:invites.${key}`, { value: broadcast.invites[key] })}
                </Typography>
              ))}

            </div>

            <Typography variant="text-sm" color={GRAY_800}>
              {t('express_broadcast_card:pushes', {
                sent: broadcast.invites.pushSent,
                failed: broadcast.invites.pushFailed,
                pending: broadcast.invites.pushPending,
              })}
            </Typography>

            {!!broadcast.winner &&
              <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
                {broadcast.acceptedAt
                  ? t('express_broadcast_card:winner_at', { name: broadcast.winner.name, dateTime: formatDateTime(broadcast.acceptedAt) })
                  : t('express_broadcast_card:winner', { name: broadcast.winner.name })
                }
              </Typography>
            }

          </div>
        }
      </div>

      {/** Revoke, only while creatives can still accept */}
      <div className={styles.buttonsWrapper}>
        {canRevoke &&
          <Button
            type="primary red"
            textAndIcon={true}
            disabled={revokeBroadcastMutation.isPending}
            isLoading={revokeBroadcastMutation.isPending}
            onClick={() => handleRevoke()}
          >
            <StopIcon fontSize='large' />
            <span>{t('express_broadcast_card:button_revoke')}</span>
          </Button>
        }
      </div>

      <QueryStatus
        spaceTopRem={2}
        className={styles.requestStatusGrid}
        successMessage={t('express_broadcast_card:revoke_success')}
        query={revokeBroadcastMutation}
        onPurge={() => revokeBroadcastMutation.reset()}
      />

    </div>
  )
}
